import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import * as moment from 'moment';
import { LazyLoadEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { filter, finalize, pluck } from 'rxjs/operators';
import { DocumentSignatureStatusType } from '../../../Foundation/models/enums/document-signature-status-type.enum';
import { CustomerCharacteristicType } from '../../../customer/models/enums/customer-characteristic-type.enum';
import { CustomerManageService } from '../../../customer/shared/customer-manage.service';
import { DocumentCategoryType } from '../../../Foundation/models/enums/document-category-type.enum';
import { DocumentType } from '../../../Foundation/models/enums/document-type.enum';
import { BaseComponent } from '../../base-components/base.component';
import { DocumentItemKeyType } from '../../enums/document-item-key-type.enum';
import { DocumentRequirementType } from '../../enums/document-requirement-type.enum';
import { formatDate, getCurrentDate, setServerDate } from '../../helpers/time-helpers.model';
import { DocumentCategoryRequirement } from '../../models/document/document-category-requirement.model';
import { DocumentData } from '../../models/document/document-data.model';
import { DocumentItemKeyData } from '../../models/document/document-itemkey.model';
import { DocumentRequirementModel } from '../../models/document/document-requirement.model';
import { DocumentSearchCriteria } from '../../models/document/document-search-criteria.model';
import { DocumentSearchResult } from '../../models/document/document-search-result.model';
import { RequiredDocumentDetailsModel } from '../../models/document/required-document-details.model';
import { DocumentService } from '../../services/document/document.service';
import { ModalSize } from '../../services/modal/mvd-modal.enum';
import { MvdModalService } from '../../services/modal/mvd-modal.service';
import { FileSaverService } from '../../services/output/file-saver.service';
import { AssistedSelfServiceInvitationContext, AssistedSelfServiceInvitationModalComponent } from '../assisted-self-service-invitation/assisted-self-service-invitation-modal.component';
import { DocumentUploadData, DocumentUploadModalComponent } from './document-upload-modal.component';
import { UpsertDocumentRequirementsData, UpsertDocumentRequirementsModalComponent } from './upsert-document-requirements-modal.component';
import { AuthService } from '../../../auth/services/auth.service';

@Component({
    selector: 'document-image',
    templateUrl: './document-image.html'
})
export class DocumentImageComponent extends BaseComponent {
    @ViewChild('dt') datatable: Table;

    @Input() hideFilterOptions = false;
    @Input() hideColumnsForComplianceManagement = false;

    public documentForm: UntypedFormGroup;
    public classApplied = false;
    public displayDocuments: DocumentCategoryRequirement[] = new Array<DocumentCategoryRequirement>();
    public documentReviewStatusTypes: any[] = new Array();
    public documentCategoryTypes: any[] = new Array();
    public documentTypes: any[] = new Array();
    public isBusinessTransaction = false;
    public isDocumentsViewRestricted: boolean;
    public readonly rows: number = 6;
    public isAllowDeleteScannedDocuments: boolean;

    // Gets the array of documents and casts it to the correct data type
    public get documentArray() {
        return this.documentForm.get('documents') as UntypedFormArray;
    }

    public totalRecords: number;
    private customerNumber: number = null;
    private dataStore: any = [];
    private documentCategoryRequirements: DocumentCategoryRequirement[];
    private documents: DocumentSearchResult[] = new Array();
    private documentRequirements: DocumentRequirementModel[] = new Array<DocumentRequirementModel>();
    private documentTypeEnum: DocumentType = new DocumentType();
    private documentCategoryTypeEnum: DocumentCategoryType = new DocumentCategoryType();
    private previousFilter = '';
    private previousPage = -1;
    private searchUnsatisfiedDocuments = true;
    private isRestrictedCustomer: boolean;
    private isACPCustomer: boolean;
    public inProgress = false;
    public maxEndDate: Date;
    private characteristics: CustomerCharacteristicType = new CustomerCharacteristicType();
    private documentRequirementType: DocumentRequirementType = new DocumentRequirementType();
    public documentSignatureStatusType: DocumentSignatureStatusType = new DocumentSignatureStatusType();

    public cols: any[] = [
        { field: 'documentTypeRequirementsSatisfied', header: 'Status' },
        { field: 'documentCategoryType', header: 'Categ' },
        { field: 'documentType', header: 'Reqmt' },
        { field: 'documentFilename', header: 'File' },
        { field: 'documentDescription', header: 'Desc' },
        { field: 'notificationComment', header: 'Comnt' },
        { field: 'updateDateDisplayValue', header: 'Updated' },
        { field: 'documentSignatureStatusTypeDisplayValue', header: 'Sig' },
        { field: 'scanLater', header: 'Scan Later' }
    ];

    /**
    * All documents saved throughout the life of this component
    */
    @Input() customerNumberId: number;
    @Input() documentsSaved: DocumentData[] = [];
    @Input() searchDocumentDataCollection: DocumentSearchCriteria[];
    @Input() documentItemKeyCollection: DocumentItemKeyData[];
    @Input() requiredDocumentDetails: RequiredDocumentDetailsModel[];
    @Input() minimalComponent = false;
    @Input() hideCloseButton = false;
    @Input() unselectableDocumentTypeIds: number[] = [];
    @Input() hideAddDocuments = false;
    @Input() hideAddRequirement = false;
    @Input() isSelectOnly = false;
    @Input() canSelectSignedDocuments = true;
    @Input() updateWithSearchCriteria = false;
    @Input() isOverrideAddDocument = false;
    @Input() selectInProgress = false;
    @Input() isViewOnly = false;

    @Output() onClose: EventEmitter<any> = new EventEmitter();
    @Output() onError: EventEmitter<any> = new EventEmitter();
    @Output() onDocumentUpload: EventEmitter<any> = new EventEmitter();
    @Output() onDocumentSelected: EventEmitter<any> = new EventEmitter();
    @Output() addDocument: EventEmitter<void> = new EventEmitter();
    @Output() onDocumentReviewStatusUpdated: EventEmitter<DocumentCategoryRequirement> = new EventEmitter();
    @Output() onDocumentRequirementAdded: EventEmitter<void> = new EventEmitter();

    constructor(private documentService: DocumentService,
        public modalService: MvdModalService,
        private fileSaverService: FileSaverService,
        private fb: UntypedFormBuilder,
        private customerService: CustomerManageService,
        private authService: AuthService) {
        super();
        this.isAllowDeleteScannedDocuments = authService.hasACSTransactionClaimEx(authService.acsTransactionClaim, 'AllowDeleteScannedDocs');
    }

    ngOnInit() {
        this.UseHelpContent('#CONTENT/AZ_MVD_Now/Manage_Customer_Documents.htm%3FTocPath%3DAZ%2520MVD%2520Now%7C_____7');
        this.displayResults = false;
        this.setDocumentItemKeyCollection();
        // Set Default Values
        this.setLookupDefaults();
        this.getDocumentLookups();
        this.checkCustomerRestriction();

        // Construct Formbuilder
        this.constructFormBuilder();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.updateWithSearchCriteria && changes && changes.searchDocumentDataCollection) {
            this.reset();
        }
    }

    private constructFormBuilder() {
        this.documentForm = this.fb.group({
            categoryTypeId: [''],
            documentTypeId: [''],
            startDate: [setServerDate(moment(getCurrentDate()).subtract(31, 'day').toDate())],
            endDate: [moment().format('l')],
            IsScanLater: [''],
            documents: this.fb.array([])
        },
        {
            validator: this.startDateValidator
        });

        this.documentForm.get('IsScanLater').disable();
        this.setEndDateMaxValue();
    }

    public checkCustomerRestriction() {
        if (this.customerNumberId > 0) {
            this.checkIfCustomerIsRestricted(this.customerNumberId);
        }
    }

    private resetSearchForm() {
        this.documentForm.get('categoryTypeId').reset();
        this.documentForm.get('documentTypeId').reset();
        this.documentForm.get('startDate').setValue(setServerDate(moment(getCurrentDate()).subtract(31, 'day').toDate()));
        this.documentForm.get('endDate').setValue(moment().format('l'));
        this.documentForm.setValidators(this.startDateValidator);
        this.setEndDateMaxValue();
    }

    public reset(): void {
        this.resetSearchForm();
        if (this.datatable) {
            this.datatable.first = 0;
        }
        this.loadResults();
    }

    public search(): void {
        this.loadResults();
    }

    private loadResults(uploadComplete: boolean = false) {
        this.inProgress = true;
        this.previousPage = -1;
        this.dataStore = [];
        this.searchUnsatisfiedDocuments = true;
        this.triggerLazyLoad(uploadComplete);
    }

    public addDocumentRequirementToDocument(documentId, documentFilename) {
        const data = new UpsertDocumentRequirementsData(this.documentItemKeyCollection, documentId, documentFilename);
        this.modalService.open(UpsertDocumentRequirementsModalComponent, data).afterClosed()
            .pipe(filter(result => !!result)).subscribe(() => {
                this.onDocumentRequirementAdded.emit();
                this.loadResults();
            });
    }

    public addDocumentToDocumentRequirement(documentCategoryTypeId, documentTypeId, documentRequirementTypeId) {

        const requiredDocumentDetails = new Array<RequiredDocumentDetailsModel>();
        const requiredDocumentDetail = new RequiredDocumentDetailsModel();
        requiredDocumentDetail.documentTypeId = documentTypeId;
        requiredDocumentDetail.documentCategoryTypeId = documentCategoryTypeId;
        requiredDocumentDetail.documentRequirementTypeId = documentRequirementTypeId;
        requiredDocumentDetails.push(requiredDocumentDetail);

        const data = new DocumentUploadData(this.searchDocumentDataCollection, this.documentItemKeyCollection, requiredDocumentDetails);
        this.modalService.open(DocumentUploadModalComponent, data).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.uploadDocumentComplete(result));
    }

    public cancel = (): void => {
        this.onClose.emit(null);
    };

    public selectDocument(documentId: number): void {
        this.onDocumentSelected.emit(documentId);
    }

    public createInvitation(document: DocumentCategoryRequirement) {
        if (document.documentId) {
            const data = new AssistedSelfServiceInvitationContext(this.customerNumber, document.documentId);
            this.modalService.open(AssistedSelfServiceInvitationModalComponent, data, ModalSize.Medium).afterClosed()
                .pipe(filter(result => !!result))
                .subscribe(
                    (data) => {
                        if (data) {
                            document.assistedSelfServiceInvitationToken = data.token;
                            document.isAssistedSelfServiceInvitationTokenExpired = data.isExpired;
                        }
                    }
                );
        }
    }

    public delete(documentCategoryRequirement: DocumentCategoryRequirement) {
        this.modalService.confirm().title('Delete Document').body('Are you sure you want to Delete the document?')
            .okBtn('Yes').cancelBtn('No')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.deleteDocumentRequirement(documentCategoryRequirement));
    }

    public downloadDocument(document) {
        if (!document.documentId) {
            document.loading = true;
            // doing in setTimeout so UI has a chance to update.  SaveDocumentToFile may take a while if the file is large.
            setTimeout(() => {
                this.saveDocumentToFile(document);
                document.loading = false;
            }, 100);
        } else {
            document.loading = true;
            this.documentService.getDocument(document.documentId).pipe(
                finalize(() => document.loading = false))
                .subscribe(
                    res => {
                        var result = res;
                        this.saveDocumentToFile(result);
                    },
                    err => {
                        this.onError.emit(err);
                    },
                    () => { }
                );
        }
    }

    public launchAddRequirementsModal() {
        const data = new UpsertDocumentRequirementsData(this.documentItemKeyCollection, null, null);
        this.modalService.open(UpsertDocumentRequirementsModalComponent, data).afterClosed()
            .pipe(filter(result => !!result)).subscribe(() => {
                this.onDocumentRequirementAdded.emit();
                this.loadResults();
            });
    }

    public launchDocumentsModal() {
        if (this.isOverrideAddDocument) {
            this.addDocument.emit();
            return;
        }

        const data = new DocumentUploadData(this.searchDocumentDataCollection, this.documentItemKeyCollection, this.requiredDocumentDetails);
        this.modalService.open(DocumentUploadModalComponent, data).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.uploadDocumentComplete(result));
    }

    public loadDataLazy(event: LazyLoadEvent, uploadComplete: boolean = false) {
        this.retrieveUploadedDocuments(event, uploadComplete);
        this.customerNumber = null;
        if (this.documentItemKeyCollection && this.documentItemKeyCollection.length) {
            for (var i = 0; i < this.documentItemKeyCollection.length; i++) {
                if (this.documentItemKeyCollection[i].documentItemKeyTypeId == DocumentItemKeyType.Customer) {

                    this.customerNumber = this.documentItemKeyCollection[i].itemKeyId;

                    // Check for the document view/download/edit restrictions.
                    this.checkIfDocumentViewIsRestricted(this.customerNumber);
                }

                if (this.documentItemKeyCollection[i].documentItemKeyTypeId == DocumentItemKeyType.BusinessTransactionNumber) {
                    this.isBusinessTransaction = true;
                }
            }
        }

        if (this.customerNumberId && this.customerNumberId > 0) {
            // Check for the document view/download/edit restrictions.
            this.checkIfDocumentViewIsRestricted(this.customerNumberId);
        }
    }

    public showAddInvitation(document: DocumentCategoryRequirement) {
        return this.customerNumber && document.documentSignatureStatusTypeId == new DocumentSignatureStatusType().RequiresSignature.id && (!document.assistedSelfServiceInvitationToken || document.isAssistedSelfServiceInvitationTokenExpired);
    }

    public showInvitationToken(document: DocumentCategoryRequirement) {
        return document.assistedSelfServiceInvitationToken && !(document.documentSignatureStatusTypeId == 2 && document.isAssistedSelfServiceInvitationTokenExpired);
    }

    public toggleClass() {
        this.classApplied = !this.classApplied;
    }

    public updateDocumentReviewStatus(documentReviewStatusTypeId, rowData) {
        if (rowData && documentReviewStatusTypeId) {
            this.documentService.updateDocumentReviewStatus(rowData.documentId, documentReviewStatusTypeId).subscribe(
                res => {
                    let documentCategoryRequirement = new DocumentCategoryRequirement();
                    documentCategoryRequirement.documentId = rowData.documentId;
                    documentCategoryRequirement.documentReviewStatusTypeId = documentReviewStatusTypeId;
                    this.onDocumentReviewStatusUpdated.emit(documentCategoryRequirement);
                    rowData.documentReviewStatusTypeId = documentCategoryRequirement.documentReviewStatusTypeId;
                },
                err => {
                    this.handleValidationResult(err);
                }
            );
        }
    }

    public updateScanLaterAll(scanLaterEvent: any) {
        const scanLater = scanLaterEvent.target.checked;
        let documentRequirements = this.documentRequirements.filter(x => !x.documentId && x.scanLater != scanLater);
        documentRequirements.forEach(x => {
            x.scanLater = scanLater;
        });

        this.updateDocumentRequirements(documentRequirements);
    }

    private addingOnToFilter(event: LazyLoadEvent) {
        return event.globalFilter != null && this.previousFilter != event.globalFilter;
    }

    private addToDisplayDocuments(event: LazyLoadEvent, documentCategoryRequirements: DocumentCategoryRequirement[]) {
        documentCategoryRequirements.forEach(item => this.dataStore.push(item));
        this.sortSliceDisplayDocuments(event);
    }

    /**
     * Builds the data structure that is needed for the UI from the array of DocumentSearchResults.
     * This is a flattend out data structure of document, documentRequirment (documentType and documentCategory)
     */
    private buildDocumentCategoryRequirements(documents: DocumentSearchResult[]): void {
        this.documentRequirements = new Array<DocumentRequirementModel>();
        if (documents != null && documents.length > 0) {
            this.documentCategoryRequirements = new Array();
            for (const document of documents) {
                if (document.documentRequirements != null && document.documentRequirements.length > 0) {
                    for (const requirement of document.documentRequirements) {
                        this.documentRequirements.push(requirement);
                        const documentCategoryRequirement = new DocumentCategoryRequirement();
                        documentCategoryRequirement.documentReviewStatusTypes = this.documentReviewStatusTypes;
                        documentCategoryRequirement.documentCategoryTypeId = requirement.documentCategoryTypeId;
                        documentCategoryRequirement.documentCategoryType = requirement.documentCategoryType;
                        documentCategoryRequirement.documentTypeRequirementsSatisfied = document.documentId && document.documentId > 0 ? true : false;
                        documentCategoryRequirement.documentId = document.documentId;
                        documentCategoryRequirement.documentFilename = document.documentFilename;
                        documentCategoryRequirement.documentReviewStatusTypeDisplayValue = document.documentReviewStatusTypeDisplayValue;
                        documentCategoryRequirement.documentReviewStatusTypeId = document.documentReviewStatusTypeId;
                        documentCategoryRequirement.documentDescription = document.documentDescription;
                        documentCategoryRequirement.notificationComment = document.notificationComment;
                        documentCategoryRequirement.documentSignatureStatusTypeId = document.documentSignatureStatusTypeId;
                        documentCategoryRequirement.documentSignatureStatusTypeDisplayValue = document.documentSignatureStatusTypeDisplayValue;
                        documentCategoryRequirement.assistedSelfServiceInvitationToken = document.assistedSelfServiceInvitationToken;
                        documentCategoryRequirement.isAssistedSelfServiceInvitationTokenExpired = document.isAssistedSelfServiceInvitationTokenExpired;
                        documentCategoryRequirement.documentTypeId = requirement.documentTypeId;
                        documentCategoryRequirement.documentType = requirement.documentType;
                        documentCategoryRequirement.documentRequirementId = requirement.id;
                        documentCategoryRequirement.documentRequirementTypeId = requirement.documentRequirementTypeId;
                        documentCategoryRequirement.loading = false;
                        documentCategoryRequirement.scanLater = requirement.scanLater;

                        if (documentCategoryRequirement.documentRequirementTypeId != null) {
                            documentCategoryRequirement.documentRequirementType = new DocumentRequirementType().FromId(documentCategoryRequirement.documentRequirementTypeId).displayValue;
                        }

                        if (documentCategoryRequirement.documentTypeRequirementsSatisfied) {
                            documentCategoryRequirement.updateDateDisplayValue = document.updatedDate ? formatDate(document.updatedDate, 'MM/DD/YYYY HH:mm') : '';
                        }

                        // CC#117141 - If customer has ACP characteristic then prevent the documents download when
                        // document is Scanned/Uploaded Documents or Credential Issuance Application
                        const isACPRedactionNeeded = this.isACPCustomer && (requirement.documentRequirementTypeId == this.documentRequirementType.CSRRequired.id ||
                            requirement.documentTypeId == this.documentTypeEnum.CredentialIssuanceApplication.id);

                        const isRestrictedCustomerRedactionNeeded = this.isRestrictedCustomer &&
                            (requirement.documentTypeId == this.documentTypeEnum.TempCredential.id ||
                                requirement.documentTypeId == this.documentTypeEnum.TempCredCDL21Older.id ||
                                requirement.documentTypeId == this.documentTypeEnum.TempCredCDLUnder21.id ||
                                requirement.documentTypeId == this.documentTypeEnum.TempCredDLPermit21Older.id ||
                                requirement.documentTypeId == this.documentTypeEnum.TempCredDLPermitUnder21.id ||
                                requirement.documentTypeId == this.documentTypeEnum.TempCredID21Older.id ||
                                requirement.documentTypeId == this.documentTypeEnum.TempCredIDUnder21.id) && localStorage.getItem('mvd:bu').indexOf('OIG') == -1;

                        documentCategoryRequirement.isDocumentsViewRestricted = isACPRedactionNeeded || isRestrictedCustomerRedactionNeeded;
                        const isUserLECU = localStorage.getItem('mvd:bu').indexOf('LECU') != -1;
                        if (!isUserLECU) {
                            const isTitleShellFromPriorDay = requirement.documentTypeId == this.documentTypeEnum.VehicleTitleShell.id &&
                                document.updatedDate != null &&
                                moment().startOf('day').diff(moment(document.updatedDate).startOf('day'), 'days') > 0;

                            if (isTitleShellFromPriorDay) {
                                documentCategoryRequirement.isDocumentsViewRestricted = true;
                            }
                        }

                        documentCategoryRequirement.isAllowDeleteScannedDocuments = (this.isAllowDeleteScannedDocuments && documentCategoryRequirement.documentRequirementTypeId == this.documentRequirementType.CSRRequired.id);

                        this.documentCategoryRequirements.push(documentCategoryRequirement);

                        // Check each document categories for a document types that has not been satisfied
                        const unsatisfiedCategories = this.documentCategoryRequirements.find(dcr => dcr.documentCategoryTypeId == requirement.documentCategoryTypeId && dcr.documentTypeRequirementsSatisfied == false);

                        // Update all grouped category instances' categoryRequirmentsSatisfied value to false if on peer has not been satisfied
                        const groupedCategory = this.documentCategoryRequirements.filter(dcr => dcr.documentCategoryTypeId == requirement.documentCategoryTypeId);
                        for (const category of groupedCategory) {
                            category.categoryRequirementsSatisfied = unsatisfiedCategories == null ? true : false;
                        }
                    }
                }
            }
        } else if (this.documents.length == 0) {
            this.documentCategoryRequirements = [];
        }
        this.displayResults = true;
    }

    /**
     * Checks if the customer documents can be downloaded.
     * @param customerNumber
     */
    private checkIfDocumentViewIsRestricted(customerNumber) {
        this.customerService.getCustomerCharacteristics(customerNumber).subscribe(
            result => {
                if (result) {
                    this.isACPCustomer = result.some(c => c.customerCharacteristicTypeId === this.characteristics.AddressConfidentialityProgram.id);

                    this.documentService.isDocumentsViewRestricted(customerNumber).subscribe(
                        result => {
                            if (result && result.result) {
                                this.isDocumentsViewRestricted = result.result;
                            } else {
                                this.isDocumentsViewRestricted = false;
                            }
                        }
                    );
                }
            }
        );
    }

    private async checkIfCustomerIsRestricted(customerNumber) {
        await this.documentService.isDocumentsViewRestricted(customerNumber).subscribe(
            result => {
                this.isRestrictedCustomer = result.result;
            }
        );
    }

    private deleteDocumentRequirement(documentCategoryRequirement: DocumentCategoryRequirement) {
        this.clearValidation();
        documentCategoryRequirement.loading = true;
        this.documentService.deleteDocumentRequirement(documentCategoryRequirement.documentRequirementId).subscribe(
            res => {
                // remove the document category requirement from the list
                this.dataStore = this.dataStore.filter(d => d.documentRequirementId !== documentCategoryRequirement.documentRequirementId);
                this.documentCategoryRequirements = this.documentCategoryRequirements.filter(d => d.documentRequirementId !== documentCategoryRequirement.documentRequirementId);
                this.documentRequirements = this.documentRequirements.filter(d => d.id !== documentCategoryRequirement.documentRequirementId);
                this.syncScanLaterAll();
                this.triggerLazyLoad();
            },
            err => {
                documentCategoryRequirement.loading = false;
                this.handleValidationResult(err);
            }
        );
    }

    private filterGlobal(row, value) {
        for (let i = 0; i < this.cols.length; i++) {
            let column: any = this.cols[i];
            if (row[column.field] == null) {
                continue;
            }
            let rowValue: String = row[column.field].toString().toLowerCase();
            if (value == null || rowValue.includes(value.toLowerCase())) {
                return true;
            }
        }
        return false;
    }

    private getAdditionalDocuments(documentSearchCriteriaList: DocumentSearchCriteria[], event: LazyLoadEvent, uploadComplete: boolean) {
        const pageNumber = this.getPageNumber(event);
        this.documentService.GetDocumentIncludingUnsatisfiedDocumentRequirementsPaginated(documentSearchCriteriaList, pageNumber, this.searchUnsatisfiedDocuments)
            .subscribe(
                res => {
                    this.displayResults = true;
                    this.documents = res.result;
                    if (res.result.length > 0) {
                        this.totalRecords = (pageNumber + 1) * this.rows + 1;
                    }
                    this.buildDocumentCategoryRequirements(this.documents);
                    this.addToDisplayDocuments(event, this.documentCategoryRequirements);
                    this.syncScanLaterAll();
                    // Document upload event should only fire if we are coming from 'uploadDocumentComplete'.
                    // We can't fire the event until here because the documentCategoryRequirements collection must be updated first.
                    if (uploadComplete) {
                        this.onDocumentUpload.emit(this.documentCategoryRequirements);
                    }
                    this.inProgress = false;
                },
                error => {
                    this.inProgress = false;
                    this.onError.emit(error);
                });
    }

    private setLookupDefaults() {
        // Manually insert all category type to drop down list
        const categoryType = { value: null, label: 'All Categories' };
        this.documentCategoryTypes.unshift(categoryType);

        // Manually insert all document type to drop down list
        const documentType = { value: null, label: 'All DocumentTypes' };
        this.documentTypes.unshift(documentType);
    }

    private getDocumentLookups() {
        this.documentService.getDocumentLookups()
            .pipe(pluck('result'), filter(result => !!result))
            .subscribe(result => {
                if (Array.isArray(result.documentReviewStatusTypes)) {
                    for (var i = 0; i < result.documentReviewStatusTypes.length; i++) {
                        this.documentReviewStatusTypes.push({ label: result.documentReviewStatusTypes[i].text, value: result.documentReviewStatusTypes[i].value });
                    }
                }
                if (Array.isArray(result.documentCategoryTypes)) {
                    /* Removing the all categories item(98) from category type list and added from the setlookupdefaults item value as undefined/null because
                    api expecting only null to fetch all records.*/
                    result.documentCategoryTypes = result.documentCategoryTypes.filter(documentType => documentType.value != this.documentCategoryTypeEnum.All.id);
                    for (var i = 0; i < result.documentCategoryTypes.length; i++) {
                        this.documentCategoryTypes.push({ label: result.documentCategoryTypes[i].text, value: result.documentCategoryTypes[i].value });
                    }
                }
                if (Array.isArray(result.documentTypes)) {
                    for (var i = 0; i < result.documentTypes.length; i++) {
                        if (!this.isSelectOnly
                            || !this.unselectableDocumentTypeIds
                            || !this.unselectableDocumentTypeIds.length
                            || !this.unselectableDocumentTypeIds.includes(+result.documentTypes[i].value)
                        ) {
                            this.documentTypes.push({ label: result.documentTypes[i].text, value: result.documentTypes[i].value });
                        }
                    }
                }
                this.displayResults = true;
            });
    }

    private getDocumentSearchCriteriaList(): DocumentSearchCriteria[] {
        const result = [];
        if (!this.searchDocumentDataCollection || this.searchDocumentDataCollection.length == 0) {
            if (this.documentItemKeyCollection && this.documentItemKeyCollection.length > 0) {
                for (let i = 0; i < this.documentItemKeyCollection.length; i++) {
                    const documentSearchCriteria = new DocumentSearchCriteria();
                    documentSearchCriteria.documentItemKeyTypeId = this.documentItemKeyCollection[i].documentItemKeyTypeId;
                    documentSearchCriteria.itemKeyId = this.documentItemKeyCollection[i].itemKeyId;
                    documentSearchCriteria.documentCategoryTypeId = this.documentForm.controls['categoryTypeId'].value;
                    documentSearchCriteria.documentTypeId = this.documentForm.controls['documentTypeId'].value;
                    documentSearchCriteria.startDate = setServerDate(moment(this.documentForm.controls['startDate'].value).startOf('day').toDate());
                    documentSearchCriteria.endDate = setServerDate(moment(this.documentForm.controls['endDate'].value).endOf('day').toDate());
                    result.push(documentSearchCriteria);
                }
            }
        } else {
            for (let j = 0; j < this.searchDocumentDataCollection.length; j++) {
                const documentSearchCriteria = new DocumentSearchCriteria();
                documentSearchCriteria.documentItemKeyTypeId = this.searchDocumentDataCollection[j].documentItemKeyTypeId;
                documentSearchCriteria.itemKeyId = this.searchDocumentDataCollection[j].itemKeyId;
                documentSearchCriteria.documentCategoryTypeId = this.documentForm.controls['categoryTypeId'].value;
                documentSearchCriteria.documentTypeId = this.documentForm.controls['documentTypeId'].value;
                documentSearchCriteria.startDate = setServerDate(moment(this.documentForm.controls['startDate'].value).startOf('day').toDate());
                documentSearchCriteria.endDate = setServerDate(moment(this.documentForm.controls['endDate'].value).endOf('day').toDate());
                result.push(documentSearchCriteria);
            }
        }
        return result;
    }

    private getPageNumber(event: LazyLoadEvent) {
        return event.first / this.rows;
    }

    private retrieveUploadedDocuments(event: LazyLoadEvent = null, uploadComplete: boolean = false) {
        const documentSearchCriteriaList = this.getDocumentSearchCriteriaList();

        if (Array.isArray(documentSearchCriteriaList) && documentSearchCriteriaList.length > 0) {

            const pageNumber = this.getPageNumber(event);
            const navigatingForward = pageNumber > this.previousPage;

            if (navigatingForward) {
                this.getAdditionalDocuments(documentSearchCriteriaList, event, uploadComplete);
                this.previousPage = pageNumber;
            } else {
                if (this.addingOnToFilter(event)) {
                    this.previousFilter = event.globalFilter;
                    this.displayDocuments = this.dataStore.filter(row => this.filterGlobal(row, event.globalFilter));

                    if (this.displayDocuments.length > 0) {
                        this.addDocumentFormControls(this.displayDocuments);
                    }
                    this.syncScanLaterAll();
                    return;

                } else if (event.globalFilter != null && !navigatingForward) {
                    event.globalFilter = null;
                } else {
                    this.syncScanLaterAll();
                }
            }
        }

        this.sortSliceDisplayDocuments(event);
    }

    /**
    * Add form controls.  Must be after the data is set since we have dynamic groups of input fields
    * based on the incoming data.  If we add them during init when a group does not exists the form will not be valid for those
    * validators that force required input
    */
    private addDocumentFormControls(documents: DocumentCategoryRequirement[]) {
        const group = {};
        documents.forEach((document) => {
            group[document.documentId] = this.fb.group({
                documentReviewStatusTypeId: [document.documentReviewStatusTypeId]
            });
        });
        this.documentForm.setControl('documents', this.fb.group(group));
    }

    private saveDocumentToFile(document: DocumentData) {
        try {
            this.fileSaverService.saveBase64ToFile(document.documentFilename, document.documentContent, document.documentMimeTypeValue);
        } catch (e) {
            this.onError.emit(e);
        }
    }

    /**
     * Checks if documentItemKeyCollection is null and if searchDocumentDataCollection is not null. If true then the documentItemKeyCollection will
     * be populated with the searchDocumentDataCollection data
     */
    private setDocumentItemKeyCollection() {
        if ((!this.documentItemKeyCollection || this.documentItemKeyCollection.length == 0) && this.searchDocumentDataCollection && this.searchDocumentDataCollection.length > 0) {

            this.documentItemKeyCollection = new Array<DocumentItemKeyData>();

            for (var i = 0; i < this.searchDocumentDataCollection.length; i++) {
                if (this.searchDocumentDataCollection[i].itemKeyId && this.searchDocumentDataCollection[i].documentItemKeyTypeId) {
                    var itemKeyData = new DocumentItemKeyData();
                    itemKeyData.documentItemKeyTypeId = this.searchDocumentDataCollection[i].documentItemKeyTypeId;
                    itemKeyData.itemKeyId = this.searchDocumentDataCollection[i].itemKeyId;
                    this.documentItemKeyCollection.push(itemKeyData);
                }
            }
        }
    }

    private sortRows(fieldName: string, order: number) {
        if (fieldName === 'updateDateDisplayValue') {
            this.dataStore.sort((a, b) => (new Date(a.updateDateDisplayValue).getTime() - new Date(b.updateDateDisplayValue).getTime()) * order);
        } else {
            this.dataStore.sort((row1, row2) => {
                const val1 = row1[fieldName];
                const val2 = row2[fieldName];
                if (val1 === val2) {
                    return 0;
                }
                let result = -1;
                if (val1 > val2) {
                    result = 1;
                }
                if (order < 0) {
                    result = -result;
                }
                return result;
            });
        }
    }

    private sortSliceDisplayDocuments(event: LazyLoadEvent) {
        this.sortRows(event.sortField, event.sortOrder);
        this.displayDocuments = this.dataStore.slice(event.first, (event.first + event.rows));
        if (this.displayDocuments.length > 0) {
            this.addDocumentFormControls(this.displayDocuments);
        }
    }

    private syncDocumentRequirements(documentRequirements: DocumentRequirementModel[], revertScanLater: boolean) {
        documentRequirements.forEach(x => {
            if (revertScanLater) {
                x.scanLater = !x.scanLater;
            }

            const documentCategoryRequirement = this.documentCategoryRequirements.find(y => y.documentRequirementId == x.id);
            if (documentCategoryRequirement) {
                documentCategoryRequirement.scanLater = x.scanLater;
            }
        });

        this.syncScanLaterAll();
    }

    private syncScanLaterAll() {
        if (!this.documentRequirements || this.documentRequirements.length == 0 || this.documentRequirements.every(x => x.documentId > 0)) {
            this.documentForm.get('IsScanLater').setValue(false);
            this.documentForm.get('IsScanLater').disable();
            return;
        }

        this.documentForm.get('IsScanLater').enable();
        const isScanLater = this.documentRequirements.find(x => !x.documentId && !x.scanLater) ? false : true;
        this.documentForm.get('IsScanLater').setValue(isScanLater);
    }

    private triggerLazyLoad(uploadComplete: boolean = false) {
        const event: LazyLoadEvent = {
            first: 0,
            rows: this.rows
        };
        this.loadDataLazy(event, uploadComplete);
    }

    private updateDocumentRequirements(documentRequirements: DocumentRequirementModel[]) {
        const validDocumentTypeIds = documentRequirements.every(req => req.documentTypeId !== 0);

        if (documentRequirements.length == 0 || !validDocumentTypeIds) {
            return;
        }

        this.syncDocumentRequirements(documentRequirements, false);

        const request = {
            documentItemKeys: this.documentItemKeyCollection,
            documentRequirements: documentRequirements
        };

        let updateFailed = false;
        this.documentService.addDocumentRequirements(request).pipe(
            finalize(() => {
                this.syncDocumentRequirements(documentRequirements, updateFailed);
            }))
            .subscribe(
                res => {
                    this.searchUnsatisfiedDocuments = true;
                },
                err => {
                    updateFailed = true;
                    this.handleValidationResult(err);
                }
            );
    }

    private uploadDocumentComplete(documentsSaved: DocumentData[]) {
        this.documentsSaved = documentsSaved;
        if (this.documentsSaved != null) {
            this.loadResults(true);
        }
    }

    public onStartDateChanged(event) {
        this.setEndDateMaxValue();
    }

    private setEndDateMaxValue() {
        const endDate = moment(this.documentForm.get('startDate').value).add(31, 'day');
        this.maxEndDate = (endDate.diff(moment(getCurrentDate()), 'days') > 0) ? getCurrentDate() : setServerDate(endDate.toDate());
        this.documentForm.get('endDate').setValue(this.maxEndDate);
    }

    private startDateValidator = (group: any): { [key: string]: boolean } => {
        if ((group.controls.startDate.value != null && group.controls.endDate.value != null)) {
            var start = new Date(group.controls.startDate.value);
            var end = new Date(group.controls.endDate.value);
            if ((start > end)) {
                return { 'startAfterEndDate': true };
            }
        }
    };
}
